/**
 * 举报审核服务
 * 提供举报审核相关的API调用
 */

import { API_CONFIG, STORAGE_KEYS } from '../config/api';

export interface ReporterReputation {
  score: number | null;
  level: string | null;
}

export interface ReportedContent {
  title: string;
  content: string;
  author: string;
  created_at: string;
  category: string;
  tags: string[];
}

export interface AIReviewResult {
  review_result: 'violation' | 'safe';
  risk_score: number;
  confidence: number;
  recommendation: 'approve' | 'review' | 'reject';
  violation_types: string[];
  reason: string;
  reviewed_at: string;
}

export interface PendingReport {
  id: number;
  content_type: string;
  content_id: number;
  content_uuid: string;
  reporter_user_id: string;
  reported_user_id: number;
  report_type: string;
  report_reason: string;
  report_evidence: string | null;
  status: string;
  reporter_ip: string;
  created_at: string;
  priority: number;
  queue_status: string;
  reporter_reputation: ReporterReputation;
  reported_content: ReportedContent | null;
  ai_review: AIReviewResult | null;
}

export interface PendingReportsResponse {
  success: boolean;
  data: {
    items: PendingReport[];
    total: number;
    page: number;
    page_size: number;
  };
  message: string;
}

export interface ReviewReportRequest {
  reportId: number;
  action: 'approve' | 'reject';
  reviewNotes?: string;
}

export interface ReviewReportResponse {
  success: boolean;
  data: {
    reportId: number;
    action: string;
    newStatus: string;
  };
  message: string;
}

class ReportReviewService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_CONFIG.BASE_URL;
  }

  private async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const token = localStorage.getItem(STORAGE_KEYS.REVIEWER_TOKEN);
    
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * 获取待审核举报列表
   */
  async getPendingReports(params: {
    page?: number;
    pageSize?: number;
    status?: string;
    priority?: number;
    content_type?: string;
  } = {}): Promise<PendingReportsResponse> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('page', (params.page || 1).toString());
      queryParams.append('pageSize', (params.pageSize || 10).toString());
      queryParams.append('status', params.status || 'pending');
      
      if (params.priority) {
        queryParams.append('priority', params.priority.toString());
      }
      
      if (params.content_type) {
        queryParams.append('content_type', params.content_type);
      }

      const response = await this.makeRequest<PendingReportsResponse>(
        `/api/simple-reviewer/pending-reports?${queryParams.toString()}`
      );

      return response;
    } catch (error) {
      console.error('Failed to fetch pending reports:', error);
      throw error;
    }
  }

  /**
   * 审核举报
   */
  async reviewReport(request: ReviewReportRequest): Promise<ReviewReportResponse> {
    try {
      const response = await this.makeRequest<ReviewReportResponse>(
        '/api/simple-reviewer/review-report',
        {
          method: 'POST',
          body: JSON.stringify(request),
        }
      );

      return response;
    } catch (error: any) {
      console.error('Failed to review report:', error);
      // 返回错误响应对象，而不是抛出错误
      return {
        success: false,
        data: {
          reportId: request.reportId,
          action: request.action,
          newStatus: ''
        },
        message: error.message || '审核失败，请稍后重试'
      };
    }
  }

  /**
   * 获取举报类型的中文标签
   */
  getReportTypeLabel(type: string): string {
    const typeMap: Record<string, string> = {
      political: '政治敏感',
      pornographic: '色情低俗',
      violent: '暴力血腥',
      privacy: '侵犯隐私',
      harassment: '骚扰辱骂',
      spam: '垃圾广告',
      fake_info: '虚假信息',
      off_topic: '偏离主题',
      other: '其他'
    };
    return typeMap[type] || type;
  }

  /**
   * 获取举报类型的颜色
   */
  getReportTypeColor(type: string): string {
    const colorMap: Record<string, string> = {
      political: 'red',
      pornographic: 'red',
      violent: 'orange',
      privacy: 'orange',
      harassment: 'volcano',
      spam: 'gold',
      fake_info: 'gold',
      off_topic: 'blue',
      other: 'default'
    };
    return colorMap[type] || 'default';
  }

  /**
   * 获取优先级的颜色
   */
  getPriorityColor(priority: number): string {
    if (priority <= 2) return 'red';
    if (priority <= 4) return 'orange';
    if (priority <= 6) return 'gold';
    return 'green';
  }

  /**
   * 获取优先级的标签
   */
  getPriorityLabel(priority: number): string {
    if (priority <= 2) return '紧急';
    if (priority <= 4) return '高';
    if (priority <= 6) return '中';
    return '低';
  }
}

export const reportReviewService = new ReportReviewService();

